import FnlSummariesAssetV4 from "~/schemas/dbt/FnlSummariesAssetV4";
import FnlSummariesTokenV4 from "~/schemas/dbt/FnlSummariesTokenV4";
import { MeasureValue } from "~/types";

// // // //

interface ZoningType {
    id: number;
    slug: string;
    name: string;
    icon_url: string | null;
    color_hex: string | null;
}

interface PropertyType {
    id: number;
    slug: string;
    name: string;
    icon_url: string | null;
    color_hex: string | null;
}

interface Issuer {
    id: string;
    slug: string;
    name: string;
    icon_url: string | null;
    color_hex: string | null;
    legal_name: null | string;
    cik: null | string;
    lei: null | string;
    legal_structure_type: null | string;
    legal_structure_country: null | string;
    legal_structure_state: null | string;
    legal_structure_country_id: null | number;
    legal_structure_country_icon_url: null | string;
}

export interface AssetRating {
    provider: ServiceProvider;
    rating: string;
    url: string;
}

interface AssetClass {
    id: number;
    slug: string;
    name: string;
    color_hex: string | null;
}

interface Protocol {
    id: number;
    slug: string;
    name: string;
    color_hex: string | null;
    icon_url: string | null;
    website: string;
}

interface Platform {
    id: number;
    slug: string;
    name: string;
    color_hex: string | null;
    icon_url: string | null;
    website: string;
    tokenization_type: string | null;
    tokenization_type_id: number | null;
    issuance_type?: "NATIVE" | "BRIDGE" | null;
}

export interface Network {
    id: number;
    slug: string;
    name: string;
    icon_url: string | null;
    color_hex: string | null;
}

interface AssetManager {
    id: number;
    slug: string;
    name: string;
    icon_url: string | null;
    color_hex: string | null;
}

export interface AssetPrimaryMarket {
    id: number;
    asset_id: number;
    base_asset_id: number;
    base_asset_ticker: string;
    contract_id: number;
    kyc_is_required: boolean;
    kyc_service_provider_id: string | null;
    kyc_service_provider_name: string | null;
    kyc_description: string | null;
    allowed_investor_types: string[];
    is_subscription_allowed: boolean;
    subscription_frequency: string | null;
    subscription_description: string;
    subscription_minimum_amount: number;
    subscription_fee_bps: number;
    is_redemption_allowed: boolean;
    redemption_frequency: string | null;
    redemption_description: string;
    redemption_minimum_amount: number;
    redemption_fee_bps: number | null;
    slug: string;
}

interface CountryMetadata {
    id: number;
    name: string;
    region_name: string;
    description: string | null;
    icon_url: string | null;
    alpha2_code: string | null;
    color_hex: string | null;
    slug: string;
}

interface ServiceProvider {
    id: number;
    slug: string;
    name: string;
    icon_url: null | string;
    color_hex: null | string;
    legal_name: null | string;
    cik: null | string;
    lei: null | string;
    legal_structure_type: null | string;
    legal_structure_country: null | string;
    legal_structure_state: null | string;
}

interface RealEstateProperty {
    id: number;
    asset_id: number;
    year_built: number | null;
    square_footage: number | null;
    bathroom_count: number | null;
    bedroom_count: number | null;
    street_address: string;
    city: string;
    state: string | null;
    zip_code: string | null;
    country_id: number;
    zoning_type_id: number;
    property_type_id: number;
    projected_rental_yield_percent: number | null;
    projected_irr_percent: number | null;
    projected_gross_development_value_dollar: number | null;
    projected_holding_period_months: number | null;
    property_type: PropertyType;
    zoning_type: ZoningType;
    country: CountryMetadata;
}

export interface AssetServiceProvider {
    id: number;
    slug: string;
    name: string;
    icon_url: null | string;
    color_hex: null | string;
    legal_name: null | string;
    cik: null | string;
    lei: null | string;
    legal_structure_type: null | string;
    legal_structure_country: null | string;
    legal_structure_state: null | string;
}

interface AssetManagerProfile extends AssetServiceProvider {
    description: string | null;
}

// // // //

export interface Token extends FnlSummariesTokenV4 {
    issuance_type: "NATIVE" | "BRIDGE" | "WRAPPER" | null;
    protocol: null | Protocol;
    base_protocol: null | Protocol;
    platform: null | Platform;
    network: Network; // NOTE - should probably be nullable for maximal type-safety
    parent_network: null | Network;
    standards: string[] | null;
    asset_manager_id: number | null; // NOTE - this can be removed from here the next time we run the FnlSummariesTokenV4 codegen
    asset_manager: null | AssetManager;
}

export interface Asset extends FnlSummariesAssetV4 {
    trailing_7_day_return_percent: number | null;
    trailing_1_month_return_percent: number | null;
    trailing_3_month_return_percent: number | null;
    trailing_1_year_return_percent: number | null;
    year_to_date_return_percent: number | null;
    since_inception_return_percent: number | null;
    management_team_description: string | null; // NOTE - remove after the next FnlSummariesAssetV4 codegen
    issuer: Issuer;
    asset_class: AssetClass;
    tokens: Token[];
    adjusted_daily_burns_dollar: MeasureValue;
    adjusted_daily_burns_token: MeasureValue;
    adjusted_daily_mints_dollar: MeasureValue;
    adjusted_daily_mints_token: MeasureValue;
    primary_market: AssetPrimaryMarket | null;
    primary_markets: AssetPrimaryMarket[] | null;
    primary_market_pegged_asset: AssetPrimaryMarket | null;
    primary_market_non_pegged_asset: AssetPrimaryMarket | null;
    pegged_asset: null | Asset;
    jurisdiction_country: null | CountryMetadata;
    ratings: null | AssetRating[];
    tokenization_type_ids: number[] | null;
    transferability_types: string[] | null;
    // NOTE - manager is actually a fnl_dim_manager row, might be different from service provider but close enough for our needs right now
    manager: null | AssetManagerProfile;
    auditor: null | AssetServiceProvider;
    crypto_broker: null | AssetServiceProvider;
    crypto_custodian: null | AssetServiceProvider;
    fund_administrator: null | AssetServiceProvider;
    paying_agent: null | AssetServiceProvider;
    sub_advisor: null | AssetServiceProvider;
    traditional_broker: null | AssetServiceProvider;
    traditional_custodian: null | AssetServiceProvider;
    transfer_agent: null | AssetServiceProvider;
    exchange_agent: null | AssetServiceProvider;
    real_estate_property: null | RealEstateProperty;
}

// // // //

export enum AssetColumns {
    id = "id",
    name = "name",
    description = "description",
    iconUrl = "icon_url",
    colorHex = "color_hex",
    unit = "unit",
    ticker = "ticker",
    isin = "isin",
    cusip = "cusip",
    website = "website",
    inceptionDate = "inception_date",
    transparencyType = "transparency_type",
    transparencyDescription = "transparency_description",
    transparencyUrl = "transparency_url",
    holderRightsDescription = "holder_rights_description",
    holderHasRightToRedeem = "holder_has_right_to_redeem",
    hasForkRights = "has_fork_rights",
    forkRightsDescription = "fork_rights_description",
    disputeResolutionCountry = "dispute_resolution_country",
    disputeResolutionDescription = "dispute_resolution_description",
    assetToPeggedAssetRatio = "asset_to_pegged_asset_ratio",
    pegMaintenanceDescription = "peg_maintenance_description",
    annualReturnTargetDescription = "annual_return_target_description",
    totalManagementFeeBps = "total_management_fee_bps",
    totalPerformanceFeeBps = "total_performance_fee_bps",
    feeStructureDescription = "fee_structure_description",
    otherFeesDescription = "other_fees_description",
    legalStructureDescription = "legal_structure_description",
    legalStructureCountryID = "legal_structure_country_id",
    legalStructureCountryIDRaw = "legal_structure__country_id",
    legalStructureCountryName = "legal_structure_country_name",
    regulatoryFramework = "regulatory_framework",
    governingBody = "governing_body",
    jurisdictionCountryID = "jurisdiction_country_id",
    jurisdictionCountryName = "jurisdiction_country_name",
    managementMechanismType = "management_mechanism_type",
    poolHoldingsAreAllSecured = "pool_holdings_are_all_secured",
    poolHoldingsSecurityDescription = "pool_holdings_security_description",
    underlyingAssetClasses = "underlying_asset_classes",
    custodyDescription = "custody_description",
    type = "type",
    slug = "slug",
    _created_at = "_created_at",
    protocolIDs = "protocol_ids",
    platformIDs = "platform_ids",
    protocolNames = "protocol_names",
    baseProtocolIDs = "base_protocol_ids",
    networkIDs = "network_ids",
    networkNames = "network_names",
    sectorIDs = "sector_ids",
    regionIDs = "region_ids",
    tokenCount = "token_count",

    // Tokenization Types
    tokenizationTypeIDs = "tokenization_type_ids",
    tokenizationSponsorshipType = "tokenization_sponsorship_type",

    transferabilityTypeIDs = "transferability_type_ids",

    // Investment strategy
    investmentStrategyTypeID = "investment_strategy_type_id",
    investmentStrategyType = "investment_strategy_type",

    // Denomination asset
    denominationAssetID = "denomination_asset_id",
    denominationAssetTicker = "denomination_asset_ticker",

    // Pool
    hasSingleHolding = "has_single_holding",

    // Distribution
    distributesIncome = "distributes_income",
    distributionFrequencyType = "distribution_frequency_type",
    navReflectsIncomeOnly = "nav_reflects_income_only",

    // Accounting
    accountingBasis = "accounting_basis",
    accountingBasisDescription = "accounting_basis_description",
    accountingFrequencyTypeID = "accounting_frequency_type_id",

    // Lockup
    lockupPeriodMonths = "lockup_period_months",
    lockupDescription = "lockup_description",

    // Term
    termLengthYears = "term_length_years",
    termDescription = "term_description",
    isOpenEnded = "is_open_ended",

    // Joined data
    issuerID = "issuer_id",
    issuerName = "issuer_name",
    assetClassID = "asset_class_id",
    assetClassName = "asset_class_name",
    peggedAssetID = "pegged_asset_id",
    peggedAssetName = "pegged_asset_name",
    peggedAssetType = "pegged_asset_type",
    managerID = "manager_id",

    // Service Providers
    subAdvisorID = "sub_advisor_id",
    traditionalBrokerID = "traditional_broker_id",
    traditionalCustodianID = "traditional_custodian_id",
    cryptoCustodianID = "crypto_custodian_id",
    cryptoBrokerID = "crypto_broker_id",
    auditorID = "auditor_id",
    fundAdministratorID = "fund_administrator_id",
    exchangeAgentID = "exchange_agent_id",
    transferAgentID = "transfer_agent_id",
    payingAgentID = "paying_agent_id",

    // facts
    // Financial Metrics
    weightedAverageMaturityYears = "weighted_average_maturity_years",
    yieldToMaturityPercent = "yield_to_maturity_percent",
    totalSupplyToken = "total_supply_token",
    circulatingSupplyToken = "circulating_supply_token",
    netAssetValueDollar = "net_asset_value_dollar",
    priceDollar = "price_dollar",
    totalAssetValueDollar = "total_asset_value_dollar",
    marketValueDollar = "market_value_dollar",
    circulatingAssetValueDollar = "circulating_asset_value_dollar",
    circulatingMarketValueDollar = "circulating_market_value_dollar",

    // Primary Market + Primary Market Fees
    primaryMarketID = "primary_market_id",
    primaryMarketSubscriptionFeeBpsV1 = "primary_market_subscription_fee_bps",
    primaryMarketRedemptionFeeBpsV1 = "primary_market_redemption_fee_bps",
    primaryMarketIsKYCRequired = "primary_market__is_kyc_required",
    primaryMarketBaseAssetTicker = "primary_market__base_asset_ticker",
    primaryMarketMinimumSubscriptionAmount = "primary_market__minimum_subscription_amt",
    primaryMarketSubscriptionDescription = "primary_market__subscription_description",
    primaryMarketSubscriptionFeeBps = "primary_market__subscription_fee_bps",
    primaryMarketRedemptionDescription = "primary_market__redemption_description",
    primaryMarketRedemptionFeeBps = "primary_market__redemption_fee_bps",
    primaryMarketInvestorTypes = "primary_market__investor_types",
    primaryMarketSubscriptionFrequency = "primary_market__subscription_frequency",
    primaryMarketRedemptionFrequency = "primary_market__redemption_frequency",
    primaryMarketKYCProviderID = "primary_market__kyc_provider_id",
    primaryMarketNonPeggedAsset = "primary_market_non_pegged_asset",
    primaryMarketPeggedAsset = "primary_market_pegged_asset",

    ////////
    ////////

    // Bridged Values
    bridgedTokenMarketCapDollar = "bridged_token_market_cap_dollar",
    bridgedTokenSupplyToken = "bridged_token_supply_token",
    bridgedTokenValueDollar = "bridged_token_value_dollar",

    // Loan metrics
    maximumCapacityDollar = "maximum_capacity_dollar",
    outstandingCapitalDollar = "outstanding_capital_dollar",
    fundedAssetsDollar = "funded_assets_dollar",
    drawndownAssetsDollar = "drawndown_assets_dollar",
    drawndownAssetsToken = "drawndown_assets_token",
    recoveredAssetsDollar = "recovered_assets_dollar",
    recoveredAssetsToken = "recovered_assets_token",
    idleCapitalDollar = "idle_capital_dollar",
    idleCapitalToken = "idle_capital_token",
    availableCapacityDollar = "available_capacity_dollar",
    availableCapacityToken = "available_capacity_token",
    outstandingPrincipalDollar = "outstanding_principal_dollar",
    outstandingPrincipalToken = "outstanding_principal_token",
    defaultedLoansAmountToken = "defaulted_loans_amount_token", // TODO - remove this?
    defaultedLoansAmountDollar = "defaulted_loans_amount_dollar",
    defaultedLoansAmountPercent = "defaulted_loans_amount_percent",
    writtenOffPrincipalDollar = "written_off_principal_dollar",
    writtenOffPrincipalToken = "written_off_principal_token",
    principalPaidDollar = "principal_paid_dollar",
    principalPaidToken = "principal_paid_token",
    interestPaidDollar = "interest_paid_dollar",
    interestPaidToken = "interest_paid_token",
    cumulativeIssuedAmountDollar = "cumulative_issued_amount_dollar",
    cumulativeIssuedAmountToken = "cumulative_issued_amount_token",
    cumulativeRepaidPrincipalDollar = "cumulative_repaid_principal_dollar",
    cumulativeRepaidPrincipalToken = "cumulative_repaid_principal_token",
    cumulativeInterestEarnedDollar = "cumulative_interest_earned_dollar",
    cumulativeInterestEarnedToken = "cumulative_interest_earned_token",
    numberOfDefaultedLoans = "number_of_defaulted_loans",
    numberOfLoansFunded = "number_of_loans_funded",
    numberOfActiveLoans = "number_of_active_loans",
    cashDragPercent = "cash_drag_percent",

    // Transaction and Activity Metrics
    adjustedDailyBurnsDollar = "adjusted_daily_burns_dollar",
    adjustedDailyBurnsToken = "adjusted_daily_burns_token",
    adjustedDailyMintsDollar = "adjusted_daily_mints_dollar",
    adjustedDailyMintsToken = "adjusted_daily_mints_token",
    dailyMintsDollar = "daily_mints_dollar",
    dailyMintsToken = "daily_mints_token",
    dailyBurnsDollar = "daily_burns_dollar",
    dailyBurnsToken = "daily_burns_token",
    trailing7DayActiveAddressesCount = "trailing_7_day_active_addresses_count",
    trailing30DayActiveAddressesCount = "trailing_30_day_active_addresses_count",
    trailing90DayActiveAddressesCount = "trailing_90_day_active_addresses_count",
    trailing365DayActiveAddressesCount = "trailing_365_day_active_addresses_count",
    dailyActiveAddressesCount = "daily_active_addresses_count",
    weeklyActiveAddressesCount = "weekly_active_addresses_count",
    monthlyActiveAddressesCount = "monthly_active_addresses_count",
    quarterlyActiveAddressesCount = "quarterly_active_addresses_count",
    annuallyActiveAddressesCount = "annually_active_addresses_count",
    holdingAddressesCount = "holding_addresses_count",
    adjustedHoldingAddressesCount = "adjusted_holding_addresses_count",
    numberOfDailyTransactionsCount = "number_of_daily_transactions_count",
    dailyTransferVolumeDollar = "daily_transfer_volume_dollar",
    dailyTransferVolumeToken = "daily_transfer_volume_token",
    weeklyTransferCount = "weekly_transfer_count",
    weeklyTransferVolume = "weekly_transfer_volume",
    monthlyTransferCount = "monthly_transfer_count",
    monthlyTransferVolume = "monthly_transfer_volume",
    trailing7DayTransferCount = "trailing_7_day_transfer_count",
    trailing7DayTransferVolume = "trailing_7_day_transfer_volume",
    trailing30DayTransferCount = "trailing_30_day_transfer_count",
    trailing30DayTransferVolume = "trailing_30_day_transfer_volume",

    m2MoneyStockDollar = "m2_money_stock_dollar",

    // Yield Metrics
    apy7Day = "apy_7_day",
    apy30Day = "apy_30_day",
    trailing7DayReturnPercent = "trailing_7_day_return_percent",
    trailing1MonthReturnPercent = "trailing_1_month_return_percent",
    trailing3MonthReturnPercent = "trailing_3_month_return_percent",
    trailing1YearReturnPercent = "trailing_1_year_return_percent",
    yearToDateReturnPercent = "year_to_date_return_percent",
    sinceInceptionReturnPercent = "since_inception_return_percent",

    // Holder concentration
    topHolderConcentration = "top_holder_concentration",
    top5HolderConcentration = "top_5_holder_concentration",
    top10HolderConcentration = "top_10_holder_concentration",

    // // // //
    // Real Estate Properties
    realEstateCountryID = "real_estate_country_id",
    realEstateCountryName = "real_estate_country_id_name",
    realEstateState = "real_estate_state",
    realEstateCity = "real_estate_city",
    realEstateZipCode = "real_estate_zip_code",
    realEstateStreetAddress = "real_estate_street_address",
    realEstatePropertyType = "real_estate_property_type",
    realEstateZoningType = "real_estate_zoning_type",
    // realEstateZoningTypeID = "real_estate_zoning_type_id",
    // realEstateZoningTypeName = "real_estate_zoning_type_name",
    // realEstatePropertyTypeID = "real_estate_property_type_id",
    // realEstatePropertyTypeName = "real_estate_property_type_name",

    dailyYieldDistributedDollar = "daily_yield_distributed_dollar",
    dailyYieldRate = "daily_yield_rate",
    hypothetical_10_000_performance = "hypothetical_10_000_performance",

    // // // //
    statsAvailable = "stats_available",
    isInvestable = "is_investable",
}
